import { useMutation } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/ui/form";
import { authClient } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { PreserverSearchLink } from "../preserver-search-link";

type MagicLinkFormValues = {
	email: string;
};

const defaultValues: MagicLinkFormValues = {
	email: "",
};

type MagicLinkFormProps = React.ComponentProps<typeof Card> & {
	redirectTo?: string;
};

export function MagicLinkForm({ redirectTo = "/", ...props }: MagicLinkFormProps) {
	const [emailSent, setEmailSent] = useState(false);

	const magicLinkSchema = z.object({
		email: z.email(m.auth_emailInvalid()),
	});

	const magicLinkMutation = useMutation({
		mutationKey: ["auth", "magic-link"],
		mutationFn: async (values: MagicLinkFormValues) => {
			const result = await authClient.signIn.magicLink({
				email: values.email,
				callbackURL: redirectTo,
			});

			if (result.error) {
				throw new Error(result.error.message ?? m.auth_magicLinkFailed());
			}

			return result.data;
		},
		meta: {
			success: { message: m.auth_magicLinkSent() },
		},
		onSuccess: () => {
			setEmailSent(true);
		},
	});

	const form = useAppForm({
		validators: { onChange: magicLinkSchema },
		defaultValues,
		onSubmit: ({ value }) => {
			magicLinkMutation.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form],
	);

	if (emailSent) {
		return (
			<Card {...props}>
				<CardHeader>
					<CardTitle>{m.auth_checkEmailTitle()}</CardTitle>
					<CardDescription>{m.auth_magicLinkCheckEmail()}</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col gap-4">
						<p className="text-muted-foreground text-sm">{m.auth_noEmailReceived()}</p>
						<Button variant="outline" onClick={() => setEmailSent(false)}>
							{m.auth_tryAgain()}
						</Button>
						<PreserverSearchLink
							to="/sign-in"
							className="text-center text-sm underline underline-offset-4"
						>
							{m.auth_backToSignIn()}
						</PreserverSearchLink>
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card {...props}>
			<CardHeader>
				<CardTitle>{m.auth_magicLinkTitle()}</CardTitle>
				<CardDescription>{m.auth_magicLinkDesc()}</CardDescription>
			</CardHeader>
			<CardContent>
				<form.AppForm>
					<form className="space-y-4" onSubmit={handleSubmit}>
						<form.AppField name="email">
							{(field) => (
								<field.TextField
									label={m.auth_email()}
									type="email"
									placeholder="m@example.com"
									autoComplete="email"
									required
								/>
							)}
						</form.AppField>
						<form.SubmitButton
							className="w-full"
							disabled={magicLinkMutation.isPending}
						>
							{magicLinkMutation.isPending
								? m.auth_sendingMagicLink()
								: m.auth_sendMagicLink()}
						</form.SubmitButton>
						<p className="text-muted-foreground text-center text-sm">
							{m.auth_rememberPassword()}{" "}
							<PreserverSearchLink
								to="/sign-in"
								className="underline underline-offset-4"
							>
								{m.auth_signIn()}
							</PreserverSearchLink>
						</p>
					</form>
				</form.AppForm>
			</CardContent>
		</Card>
	);
}
